import { useEffect } from 'react';
import { toast } from 'sonner';
import { isApprovalDismissible, type Estimate } from '@/types/estimate';

interface UseEstimateEditGuardArgs {
  isEditMode: boolean;
  editId?: string;
  existingEstimate?: Estimate;
  onRedirect: (id: string) => void;
}

/**
 * Statuses the edit form opens without ceremony. APPROVED is admitted too,
 * through `isApprovalDismissible`: the form then warns that saving dismisses
 * the approval, and the save confirms it. Mirrors the backend's
 * `EDITABLE_STATUSES` + `approvalDismissalBlocker`.
 */
const EDITABLE_STATUSES = ['DRAFT', 'REVIEW', 'REVISED', 'SENT', 'VIEWED', 'FOLLOW_UP'];

export function useEstimateEditGuard({
  isEditMode,
  editId,
  existingEstimate,
  onRedirect,
}: UseEstimateEditGuardArgs) {
  useEffect(() => {
    if (!isEditMode || !existingEstimate || !editId) return;

    if (EDITABLE_STATUSES.includes(existingEstimate.status)) return;
    if (isApprovalDismissible(existingEstimate)) return;

    toast.error(
      existingEstimate.status === 'APPROVED'
        ? 'This approved work already belongs to a job. Edit the job, or raise a change order, instead of the estimate.'
        : 'This estimate cannot be edited in its current status.',
    );
    onRedirect(editId);
  }, [isEditMode, existingEstimate, editId, onRedirect]);
}
